import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Tabs,
  Tab,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Grid,
  IconButton,
  Alert,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Notifications as NotificationIcon,
  NotificationsActive as NotifActiveIcon,
  Dashboard as DashboardIcon,
  Palette as ThemeIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { settingsService, UserSettings } from '../services/settingsService';
import { useThemeSettings, COLOR_MAP, resolveColor } from '../contexts/ThemeContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Settings() {
  const { themeSettings, setThemeSettings } = useThemeSettings();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [browserPermission, setBrowserPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );

  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const userSettings = await settingsService.getSettings();
      setSettings(userSettings);
      if (userSettings.theme) {
        setThemeSettings({
          mode: userSettings.theme.mode ?? 'light',
          primaryColor: userSettings.theme.primaryColor ?? 'blue',
        });
      }
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  }, [setThemeSettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const saveSettings = async () => {
    if (!settings) return;
    try {
      setLoading(true);
      const savedSettings = await settingsService.updateSettings(settings);
      setSettings(savedSettings);
      toast.success('Settings saved successfully');
    } catch {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !settings) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load settings</Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>Settings</Typography>

      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} variant="scrollable">
            <Tab label="Notifications" icon={<NotificationIcon />} iconPosition="start" />
            <Tab label="Dashboard" icon={<DashboardIcon />} iconPosition="start" />
            <Tab label="Theme" icon={<ThemeIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Notifications Tab */}
        <TabPanel value={tabValue} index={0}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} spacing={2} justifyContent="space-between">
                  <Box>
                    <Typography variant="h6" gutterBottom>Browser Notification Permission</Typography>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      {browserPermission === 'granted' && <><CheckCircleIcon color="success" fontSize="small" /><Typography variant="body2" color="success.main">Granted — browser notifications are active</Typography></>}
                      {browserPermission === 'denied' && <><CancelIcon color="error" fontSize="small" /><Typography variant="body2" color="error">Denied — please allow in browser site settings</Typography></>}
                      {browserPermission === 'default' && <><NotifActiveIcon color="warning" fontSize="small" /><Typography variant="body2" color="text.secondary">Not yet requested</Typography></>}
                    </Stack>
                  </Box>
                  {browserPermission !== 'granted' && browserPermission !== 'denied' && (
                    <Button
                      variant="contained"
                      startIcon={<NotifActiveIcon />}
                      onClick={async () => {
                        if (typeof Notification === 'undefined') {
                          toast.error('Browser does not support notifications');
                          return;
                        }
                        const result = await Notification.requestPermission();
                        setBrowserPermission(result);
                        if (result === 'granted') {
                          toast.success('Browser notifications enabled');
                          new Notification('RideSaathi', { body: 'Notifications are now active!' });
                        } else {
                          toast.error('Permission denied');
                        }
                      }}
                    >
                      Enable Browser Notifications
                    </Button>
                  )}
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>General</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.enabled}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: { ...prev.notifications, enabled: e.target.checked },
                      } : prev)}
                    />
                  }
                  label="Enable all notifications"
                  sx={{ mb: 1, display: 'flex' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.emailNotifications}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: { ...prev.notifications, emailNotifications: e.target.checked },
                      } : prev)}
                    />
                  }
                  label="Email notifications"
                  sx={{ mb: 1, display: 'flex' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.pushNotifications}
                      disabled={browserPermission !== 'granted'}
                      onChange={(e) => {
                        if (e.target.checked && browserPermission !== 'granted') {
                          toast.error('Grant browser permission first');
                          return;
                        }
                        setSettings(prev => prev ? {
                          ...prev,
                          notifications: { ...prev.notifications, pushNotifications: e.target.checked },
                        } : prev);
                      }}
                    />
                  }
                  label={`Push notifications${browserPermission !== 'granted' ? ' (grant permission above first)' : ''}`}
                  sx={{ mb: 1, display: 'flex' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.soundEnabled}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: { ...prev.notifications, soundEnabled: e.target.checked },
                      } : prev)}
                    />
                  }
                  label="Sound alerts"
                  sx={{ display: 'flex' }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Alert Types</Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.rideAlerts.newRide}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          rideAlerts: { ...prev.notifications.rideAlerts, newRide: e.target.checked },
                        },
                      } : prev)}
                    />
                  }
                  label="New ride requests"
                  sx={{ mb: 1, display: 'flex' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.rideAlerts.rideUpdate}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          rideAlerts: { ...prev.notifications.rideAlerts, rideUpdate: e.target.checked },
                        },
                      } : prev)}
                    />
                  }
                  label="Ride status updates"
                  sx={{ mb: 1, display: 'flex' }}
                />
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.notifications.rideAlerts.rideCompletion}
                      onChange={(e) => setSettings(prev => prev ? {
                        ...prev,
                        notifications: {
                          ...prev.notifications,
                          rideAlerts: { ...prev.notifications.rideAlerts, rideCompletion: e.target.checked },
                        },
                      } : prev)}
                    />
                  }
                  label="Ride completions"
                  sx={{ display: 'flex' }}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Paper sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Box>
                    <Typography variant="h6">Quiet Hours</Typography>
                    <Typography variant="body2" color="text.secondary">Suppress notifications during these time windows</Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => setSettings(prev => prev ? {
                      ...prev,
                      notifications: {
                        ...prev.notifications,
                        periods: [...prev.notifications.periods, { start: '22:00', end: '07:00', enabled: true }],
                      },
                    } : prev)}
                  >
                    Add Window
                  </Button>
                </Box>
                {settings.notifications.periods.length === 0 ? (
                  <Alert severity="info">No quiet hours configured. Notifications will be sent at any time.</Alert>
                ) : (
                  <Stack spacing={2}>
                    {settings.notifications.periods.map((period, idx) => (
                      <Box key={idx} display="flex" alignItems="center" gap={2} flexWrap="wrap">
                        <Switch
                          size="small"
                          checked={period.enabled}
                          onChange={(e) => {
                            const updated = [...settings.notifications.periods];
                            updated[idx] = { ...period, enabled: e.target.checked };
                            setSettings(prev => prev ? { ...prev, notifications: { ...prev.notifications, periods: updated } } : prev);
                          }}
                        />
                        <TextField
                          size="small" type="time" label="From"
                          value={period.start}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            const updated = [...settings.notifications.periods];
                            updated[idx] = { ...period, start: e.target.value };
                            setSettings(prev => prev ? { ...prev, notifications: { ...prev.notifications, periods: updated } } : prev);
                          }}
                        />
                        <TextField
                          size="small" type="time" label="To"
                          value={period.end}
                          InputLabelProps={{ shrink: true }}
                          onChange={(e) => {
                            const updated = [...settings.notifications.periods];
                            updated[idx] = { ...period, end: e.target.value };
                            setSettings(prev => prev ? { ...prev, notifications: { ...prev.notifications, periods: updated } } : prev);
                          }}
                        />
                        <IconButton
                          color="error" size="small"
                          onClick={() => {
                            const updated = settings.notifications.periods.filter((_, i) => i !== idx);
                            setSettings(prev => prev ? { ...prev, notifications: { ...prev.notifications, periods: updated } } : prev);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Dashboard Tab */}
        <TabPanel value={tabValue} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Dashboard Layout</Typography>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Layout Style</InputLabel>
                  <Select
                    value={settings.dashboard.layout}
                    onChange={(e) => setSettings(prev => prev ? {
                      ...prev,
                      dashboard: { ...prev.dashboard, layout: e.target.value as 'grid' | 'list' | 'compact' },
                    } : prev)}
                    label="Layout Style"
                  >
                    <MenuItem value="grid">Grid</MenuItem>
                    <MenuItem value="list">List</MenuItem>
                    <MenuItem value="compact">Compact</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  label="Refresh Interval (seconds)"
                  type="number"
                  value={settings.dashboard.refreshInterval}
                  onChange={(e) => setSettings(prev => prev ? {
                    ...prev,
                    dashboard: { ...prev.dashboard, refreshInterval: parseInt(e.target.value) || 30 },
                  } : prev)}
                />
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Theme Tab */}
        <TabPanel value={tabValue} index={2}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>Theme Settings</Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Changes apply immediately across the app.
                </Typography>
                <FormControl fullWidth sx={{ mb: 3 }}>
                  <InputLabel>Theme Mode</InputLabel>
                  <Select
                    value={themeSettings.mode}
                    onChange={(e) => {
                      const mode = e.target.value as 'light' | 'dark' | 'auto';
                      setThemeSettings({ ...themeSettings, mode });
                      setSettings(prev => prev ? { ...prev, theme: { ...prev.theme, mode } } : prev);
                    }}
                    label="Theme Mode"
                  >
                    <MenuItem value="light">☀️ Light</MenuItem>
                    <MenuItem value="dark">🌙 Dark</MenuItem>
                    <MenuItem value="auto">🖥️ Follow System</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="subtitle2" gutterBottom>Primary Color</Typography>
                <Stack direction="row" spacing={1.5} flexWrap="wrap" mb={3}>
                  {Object.entries(COLOR_MAP).map(([name, hex]) => (
                    <Tooltip key={name} title={name.charAt(0).toUpperCase() + name.slice(1)}>
                      <Box
                        onClick={() => {
                          setThemeSettings({ ...themeSettings, primaryColor: name });
                          setSettings(prev => prev ? { ...prev, theme: { ...prev.theme, primaryColor: name } } : prev);
                        }}
                        sx={{
                          width: 36, height: 36, borderRadius: '50%', bgcolor: hex, cursor: 'pointer',
                          border: themeSettings.primaryColor === name ? '3px solid' : '2px solid transparent',
                          borderColor: themeSettings.primaryColor === name ? 'text.primary' : 'transparent',
                          boxShadow: themeSettings.primaryColor === name ? 2 : 0,
                          transition: 'all .15s',
                        }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
                <Alert severity="info" icon={false}>
                  Current: <strong>{themeSettings.mode}</strong> mode,{' '}
                  <span style={{ color: resolveColor(themeSettings.primaryColor), fontWeight: 700 }}>
                    {themeSettings.primaryColor}
                  </span> accent. Click Save to persist.
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </TabPanel>

        <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={saveSettings}
            disabled={loading}
          >
            {loading ? 'Saving...' : 'Save Settings'}
          </Button>
        </Box>
      </Card>
    </Box>
  );
}
